import React, {Component} from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import {Field, change, untouch} from 'redux-form';
import {connect} from 'react-redux';
import {compose} from 'recompose';
import {RESOURCES as locationResources} from '../fuseLocationsResourceMap';
import {copyArray} from '../shared/functions';
import {checkTokenStatus} from '../shared/tokenFunctions';
import AdditionalTaxTableRow from '../components/AdditionalTaxTableRow';
import VirtualizedSelectInput from '../components/VirtualizedSelectInput';
import FlatButton from 'material-ui/FlatButton';
import ContentAdd from 'material-ui/svg-icons/content/add';
import {
    Table,
    TableHeader,
    TableHeaderColumn,
    TableRow,
    TableBody
} from 'material-ui';

/**
 * A component for adding and removing AccessTableRows
 *
 */
class AdditionalTaxesTable extends Component {
    constructor(props) {
        super(props);
        this.state = {
            additionalTaxChoices: [],
            additionalTaxObjects: [],
            taxSelectValue: null
        };
        this.handleAddTax = this.handleAddTax.bind(this);
        this.handleRemoveTax = this.handleRemoveTax.bind(this);
    }

    componentDidMount() {
        if (this.props.clearList) {
            this.props.dispatch(change('record-form', 'additional_taxes', []));
        }
        this.fetchAdditionalTaxChoices();
    }

    // Get data for adding to Additional Taxes list
    fetchAdditionalTaxChoices() {
        const params = {
            filter: {},
            pagination: {page: 1, perPage: 10000},
            sort: {field: 'name', order: 'ASC'}
        };
        let resource = locationResources.AdditionalTaxes.GET_LIST(params);

        // Retrieve the data and assign results to the Select choices
        import('../shared/functions')
            .then(functions => {
                const tokenStatus = checkTokenStatus(null);
                if (tokenStatus.expired) {
                    return;
                }
                axios({
                    method: 'get',
                    url: resource.uri,
                    headers: functions.getGetHeaders()
                }).then((result) => {
                    if (result.data.payload) {
                        let choices = this.setChoiceKeys(result.data.payload);
                        this.populateTaxObjects(choices);
                    }
                }).catch((error) => {
                    let message = '';
                    if (error.response) {
                        console.log(error.response.data);
                        if (error.response.data.error_message) {
                            message = error.response.data.error_message;
                            console.log('Error: ' + message);
                        }
                    } else if (error.request) {
                        console.log(error.request);
                    } else {
                        console.log('Error: ', error.message);
                    }
                });
            });
    }

    // Add unique keys for choices and display
    setChoiceKeys = (choices) => {
        for (let choice of choices) {
            choice.id = choice.machine_name;
            choice.tax_rate_percent = choice.tax_rate + '%';
            choice.display_name = choice.name + ' - ' + choice.tax_rate_percent;
            if (choice.description) {
                choice.display_name += ' - ' + choice.description;
            }
        }
        return choices;
    };

    // Populate Additional Tax table
    populateTaxObjects = (choices) => {
        let additionalTaxes = (this.props.additional_taxes) ? copyArray(this.props.additional_taxes) : [];
        let additionalTaxObjects = (this.state.additionalTaxObjects) ? copyArray(this.state.additionalTaxObjects) : [];

        for (let choice of choices) {
            let item = additionalTaxes.find(s => s === choice.machine_name);
            if (item) {
                additionalTaxObjects.push(choice);
            }
        }
        this.props.dispatch(change('record-form', 'additional_tax_objects', additionalTaxObjects));
        this.setState({
            additionalTaxChoices: choices,
            additionalTaxObjects: additionalTaxObjects
        });
    };

    // Create a new item based on field values
    handleAddTax = (e) => {
        const selectedValue = this.props.selectValue;
        if (!selectedValue) {
            return;
        }

        let additionalTaxChoices = copyArray(this.state.additionalTaxChoices);
        let additionalTaxObjects = copyArray(this.state.additionalTaxObjects);
        // Get the full Additional Tax object from the available choices
        let item = additionalTaxChoices.find(s => s.machine_name === selectedValue);
        if (item) {
            if (additionalTaxObjects.findIndex(t => t.machine_name === item.machine_name) === -1) {
                additionalTaxObjects.push(item);
            }

            this.props.dispatch(change('record-form', 'additional_tax_objects', additionalTaxObjects));
            this.setState({
                additionalTaxObjects: additionalTaxObjects,
                taxSelectValue: null,
            }, () => {
                // Reset select input
                this.props.dispatch(change('record-form', 'additionalTaxSelectField', ''));
                this.props.dispatch(untouch('record-form', 'additionalTaxSelectField'));
            });
        }
    };

    // Handle event from item Remove icon
    handleRemoveTax = (machine_name) => {
        let additionalTaxObjects = copyArray(this.state.additionalTaxObjects);
        // Remove item from list component if it exists
        let updatedAdditionalTaxObjects = additionalTaxObjects.filter(t => t.machine_name !== machine_name);
        if (updatedAdditionalTaxObjects) {
            this.props.dispatch(change('record-form', 'additional_tax_objects', updatedAdditionalTaxObjects));
            this.setState({
                additionalTaxObjects: updatedAdditionalTaxObjects,
                taxSelectValue: null,
            });
        }
    };

    // Create table rows of added items
    makeAdditionalTaxesTable = () => {
        let additionalTaxObjects = copyArray(this.state.additionalTaxObjects);
        return (
            additionalTaxObjects.map((item, index) => (
                <AdditionalTaxTableRow
                    key={`${item.machine_name}-${index}`}
                    item={item}
                    showRemove={this.props.showRemove}
                    onRemoveClick={this.handleRemoveTax}
                />
            ))
        )
    };

    render() {
        const {
            additionalTaxChoices,
            taxSelectValue
        } = this.state;

        const {
            showAdd,
            showRemove
        } = this.props;

        const styles = {
            addBtn: {marginBottom: 6, verticalAlign: 'bottom'},
            fieldDescription: {
                display: 'block',
                width: '100%',
                fontSize: '14px',
                margin: '0 0 1em 0',
            },
            fieldCreate: {display: 'inline-block', margin: '0 1em 0 0', verticalAlign: 'top'},
            row: {minHeight: '40px', verticalAlign: 'bottom'},
            rowSmall: {minHeight: '40px', maxWidth: 100, verticalAlign: 'bottom'},
            rowWrap: {minHeight: '40px', whiteSpace: 'wrap', verticalAlign: 'bottom'},
        };

        return (
            <div>
                {showAdd && additionalTaxChoices && additionalTaxChoices.length > 0 &&
                    <div>
                        <Field name="additionalTaxesSelectField"
                               component={VirtualizedSelectInput}
                               choices={additionalTaxChoices}
                               label="Select an Additional Tax"
                               optionText="display_name"
                               defaultValue={taxSelectValue}
                               style={styles.fieldCreate}
                               elStyle={styles.fieldCreate}
                               allowEmpty
                               options={{
                                   className: "full-width-with-button inline",
                                   placeholder: "Select an Additional Tax",
                               }}
                        />
                        <FlatButton
                            primary
                            label="Add"
                            icon={<ContentAdd/>}
                            onClick={(e) => this.handleAddTax(e)}
                            style={styles.addBtn}
                        />
                    </div>
                }
                <Table selectable={false} multiSelectable={false}>
                    <TableHeader displaySelectAll={false} adjustForCheckbox={false}>
                        <TableRow>
                            <TableHeaderColumn style={styles.row}>
                                <span>Additional Tax</span>
                            </TableHeaderColumn>
                            <TableHeaderColumn style={styles.row}>
                                <span>Description</span>
                            </TableHeaderColumn>
                            <TableHeaderColumn style={styles.rowSmall}>
                                <span>Tax Rate %</span>
                            </TableHeaderColumn>
                            {showRemove &&
                                <TableHeaderColumn style={styles.rowSmall}>
                                    <span>Remove</span>
                                </TableHeaderColumn>
                            }
                        </TableRow>
                    </TableHeader>
                    <TableBody displayRowCheckbox={false}>
                        {[...this.makeAdditionalTaxesTable()]}
                    </TableBody>
                </Table>
            </div>
        )
    }
}

const mapStateToProps = (state, ownProps) => {
    let additionalTaxes = (ownProps.additional_taxes) ? ownProps.additional_taxes : [];
    let form = state.form['record-form'];
    let additionalTaxObjects = (form && form.values && form.values['additional_tax_objects']) ? form.values['additional_tax_objects'] : [];
    let selectValue = (form && form.values && form.values['additionalTaxesSelectField']) ? form.values['additionalTaxesSelectField'].toString() : null;
    return {
        additionalTaxes: additionalTaxes,
        additionalTaxObjects: additionalTaxObjects,
        selectValue: selectValue,
    }
};

AdditionalTaxesTable.propTypes = {
    addField: PropTypes.bool,
    clearList: PropTypes.bool,
    onAddUpdate: PropTypes.func,
    onRemoveUpdate: PropTypes.func,
    selectValue: PropTypes.string,
    showAdd: PropTypes.bool,
    showRemove: PropTypes.bool
};

AdditionalTaxesTable.defaultProps = {
    addField: true,
    clearList: false,
    selectValue: null,
    showAdd: false,
    showRemove: false
};

const enhance = compose(
    connect(mapStateToProps)
);

export default enhance(AdditionalTaxesTable);



// WEBPACK FOOTER //
// ./src/Fuse/components/AdditionalTaxesTable.js